import React from 'react';

import { useTranslation } from 'react-i18next';

import cls from './FinanceOverview.module.css';

interface FinanceOverviewProps {
    dashboardUrl: string;
    reportsUrl: string;
    canViewDashboard: boolean;
}

const ChartPreview = () => (
    <div className={`${cls.visual} ${cls.chartVisual}`} aria-hidden="true">
        <div className={cls.visualWindow}>
            <div className={cls.visualToolbar}>
                <i />
                <i />
                <i />
                <span />
            </div>
            <div className={cls.visualKpis}>
                <i />
                <i />
                <i />
            </div>
            <div className={cls.visualChartGrid}>
                <div className={cls.visualBars}>
                    {[37, 58, 45, 76, 67, 88].map((height) => (
                        <i key={height} style={{ height: `${height}%` }} />
                    ))}
                </div>
                <svg viewBox="0 0 420 155" preserveAspectRatio="none">
                    <path
                        className={cls.visualArea}
                        d="M0 123 C42 116 55 102 88 105 S137 91 171 94 S221 66 254 75 S309 54 340 59 S390 28 420 37 L420 155 L0 155Z"
                    />
                    <path
                        className={cls.visualLine}
                        d="M0 123 C42 116 55 102 88 105 S137 91 171 94 S221 66 254 75 S309 54 340 59 S390 28 420 37"
                    />
                    <circle cx="420" cy="37" r="5" />
                </svg>
            </div>
        </div>
    </div>
);

const TablePreview = () => (
    <div className={`${cls.visual} ${cls.tableVisual}`} aria-hidden="true">
        <div className={cls.visualWindow}>
            <div className={cls.visualToolbar}>
                <i />
                <i />
                <i />
                <span />
            </div>
            <div className={cls.tableHead}>
                <i />
                <i />
                <i />
                <i />
            </div>
            {Array.from({ length: 6 }, (_, index) => (
                <div className={cls.tableRow} key={index}>
                    <i />
                    <i />
                    <i />
                    <i />
                </div>
            ))}
        </div>
    </div>
);

export const FinanceOverview = ({
    dashboardUrl,
    reportsUrl,
    canViewDashboard,
}: FinanceOverviewProps) => {
    const { t } = useTranslation();

    const dashboardContent = (
        <>
            <div className={cls.cardCopy}>
                <span className={cls.kicker}>{t('FinanceOverview.dashboard.kicker')}</span>
                <h2>{t('FinanceOverview.dashboard.title')}</h2>
                <p>{t('FinanceOverview.dashboard.description')}</p>
                <span className={cls.accessNote}>
                    <span aria-hidden="true">🔒</span>
                    {t('FinanceOverview.dashboard.accessNote')}
                </span>
                <span className={cls.action}>
                    {t('FinanceOverview.dashboard.action')} <b aria-hidden="true">→</b>
                </span>
            </div>
            <ChartPreview />
        </>
    );

    return (
        <main className={cls.page}>
            <header className={cls.header}>
                <h1>{t('FinanceOverview.header.title')}</h1>
                <p>{t('FinanceOverview.header.subtitle')}</p>
            </header>

            <section className={cls.choice} aria-label={t('FinanceOverview.choice.ariaLabel')}>
                {canViewDashboard ? (
                    <a className={cls.card} href={dashboardUrl}>
                        {dashboardContent}
                    </a>
                ) : (
                    <div className={`${cls.card} ${cls.disabled}`} aria-disabled="true">
                        {dashboardContent}
                    </div>
                )}

                <a className={cls.card} href={reportsUrl}>
                    <div className={cls.cardCopy}>
                        <span className={cls.kicker}>{t('FinanceOverview.reports.kicker')}</span>
                        <h2>{t('FinanceOverview.reports.title')}</h2>
                        <p>{t('FinanceOverview.reports.description')}</p>
                        <span className={cls.action}>
                            {t('FinanceOverview.reports.action')} <b aria-hidden="true">→</b>
                        </span>
                    </div>
                    <TablePreview />
                </a>
            </section>
        </main>
    );
};
